import { format } from "date-fns";
import { t } from "i18next";
import { get, isEmpty } from "lodash";
import { isValidDate } from "../../../utils";
import { getLogicSymbol } from "../../PatientTags/constants";

// Helper function to convert date to UTC ISO string
// Format matches the backend format for remind_after: '%Y-%m-%dT%H:%M:%S.%fZ'
// where %f expects 6 digits for microseconds (not 3 for milliseconds)
const toUTCISOString = (date) => {
  if (!date) return null;
  const d = new Date(date);
  // Get the ISO string and replace milliseconds (3 digits) with microseconds (6 digits)
  const isoString = d.toISOString();
  // Replace .000Z with .000000Z to match backend format
  return isoString.replace(/\.(\d{3})Z$/, (match, milliseconds) => {
    // Pad milliseconds to 6 digits (microseconds)
    const microseconds = milliseconds.padEnd(6, "0");
    return `.${microseconds}Z`;
  });
};

// Helper function to get timezone offset in minutes (Local - UTC)
const getTimezoneOffset = () => {
  return -new Date().getTimezoneOffset(); // Negate because getTimezoneOffset returns UTC - Local
};

// Helper function to parse date strings like "today", "today+7d", etc. to Date objects
const parseDateString = (dateString) => {
  if (!dateString) return null;

  // If it's already an ISO date string, parse it directly
  if (dateString.includes("T") || dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
    return new Date(dateString);
  }

  // Handle special date strings
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to start of day

  if (dateString === "today") {
    return today;
  }

  // Handle patterns like "today+7d", "today-1d", etc.
  const match = dateString.match(/^today([+-])(\d+)d?$/i);
  if (match) {
    const operator = match[1];
    const days = parseInt(match[2], 10);
    const result = new Date(today);
    if (operator === "+") {
      result.setDate(result.getDate() + days);
    } else {
      result.setDate(result.getDate() - days);
    }
    return result;
  }

  // If we can't parse it, try to create a Date object anyway
  return new Date(dateString);
};

// Helper function to parse reminders structure and extract task filter values
// This is the reverse of buildReminders - it extracts UI filter values from the reminders structure
const parseRemindersToTaskFilters = (reminders) => {
  if (!reminders || !reminders.groups || !Array.isArray(reminders.groups)) {
    return {
      taskDueDateRange: [null, null],
      task_status: [],
      task_owner: [],
    };
  }

  const result = {
    taskDueDateRange: [null, null],
    task_status: [],
    task_owner: [],
  };

  reminders.groups.forEach((group) => {
    // Parse custom_due_date group
    if (
      group.id === "custom_due_date" &&
      group.conditions &&
      group.conditions.length > 0
    ) {
      const condition = group.conditions[0];
      if (condition.constraints) {
        const startDateStr = condition.constraints.remind_after__gte;
        const endDateStr = condition.constraints.remind_after__lte;
        if (startDateStr && endDateStr) {
          // Parse date strings (handles both ISO strings and special strings like "today", "today+7d")
          const startDate = parseDateString(startDateStr);
          const endDate = parseDateString(endDateStr);
          if (startDate && endDate) {
            result.taskDueDateRange = [startDate, endDate];
          }
        }
      }
    }

    // Parse task_status group
    if (
      group.id === "task_status" &&
      group.conditions &&
      Array.isArray(group.conditions)
    ) {
      const statuses = [];
      group.conditions.forEach((condition) => {
        if (condition.id === "overdue") {
          statuses.push("overdue");
        } else if (condition.id === "urgent") {
          statuses.push("urgent");
        } else if (condition.id === "upcoming") {
          statuses.push("upcoming");
        } else if (condition.id === "done") {
          statuses.push("done");
        }
      });
      result.task_status = statuses;
    }

    // Parse task_owner group
    if (group.id === "task_owner" && group.conditions && group.conditions.length > 0) {
      const condition = group.conditions[0];
      if (condition.constraints && condition.constraints.owned_by_id__in) {
        // Convert number IDs to strings to match UI format
        result.task_owner = condition.constraints.owned_by_id__in.map((id) => String(id));
      }
    }
  });

  return result;
};

// Helper function to build reminders structure
const buildReminders = (filters) => {
  const groups = [];

  // Get timezone offset
  const timezoneOffset = getTimezoneOffset();

  // Task Due Date Range group
  const taskDueDateRange = get(filters, ["taskDueDateRange"], [null, null]);
  if (taskDueDateRange[0] && taskDueDateRange[1]) {
    const startDate = toUTCISOString(taskDueDateRange[0]);
    const endDate = toUTCISOString(taskDueDateRange[1]);

    groups.push({
      id: "custom_due_date",
      logic: "AND",
      conditions: [
        {
          id: "due_date",
          constraints: {
            remind_after__gte: startDate,
            remind_after__lte: endDate,
          },
        },
      ],
    });
  }

  // Task Status group
  const taskStatus = get(filters, ["task_status"], []);
  if (taskStatus.length > 0) {
    const conditions = [];

    taskStatus.forEach((status) => {
      if (status === "overdue") {
        conditions.push({
          id: "overdue",
          constraints: {
            remind_after__lt: "today",
            resolved: false,
          },
        });
      } else if (status === "urgent") {
        conditions.push({
          id: "urgent",
          constraints: {
            remind_after__gte: "today",
            remind_after__lte: "today+7d",
            resolved: false,
          },
        });
      } else if (status === "upcoming") {
        conditions.push({
          id: "upcoming",
          constraints: {
            remind_after__gt: "today+7d",
            resolved: false,
          },
        });
      } else if (status === "done") {
        conditions.push({
          id: "done",
          constraints: {
            resolved: true,
          },
        });
      }
    });

    if (conditions.length > 0) {
      groups.push({
        id: "task_status",
        logic: "OR",
        conditions,
      });
    }
  }

  // Task Owner group
  const taskOwnerIds = get(filters, ["task_owner"], []);
  if (taskOwnerIds.length > 0) {
    // Convert string IDs to numbers
    const ownerIds = taskOwnerIds.map((id) => Number(id)).filter((id) => !isNaN(id));

    if (ownerIds.length > 0) {
      groups.push({
        id: "task_owner",
        logic: "AND",
        conditions: [
          {
            id: "task_owner",
            constraints: {
              owned_by_id__in: ownerIds,
            },
          },
        ],
      });
    }
  }

  // Only return reminders if there are groups
  if (groups.length > 0) {
    return {
      timezone_offset: timezoneOffset,
      groups,
    };
  }

  return null;
};

const formatFiltersObj = (filters, specialTagIds = null) => {
  const { dateRange, patient_type } = filters;

  const categories = {
    ai_tags: {
      present_findings: {
        tags: [],
        join: "",
      },
      absent_findings: {
        tags: [],
        join: "",
      },
    },
    workflow_tags: {
      present_findings: {
        tags: [],
        join: "",
      },
      absent_findings: {
        tags: [],
        join: "",
      },
    },
    lab_results: {},
    patient_type: {},
    custom_tags: [],
  };
  const genders = [];
  const ageGroups = [];
  const sites = [];
  const dicom_upload = [];
  const report_editors = [];
  const symptoms = get(filters, ["symptoms", "tags", "dropdownMenuTags"], []);
  const risk_groups = get(filters, ["risk_groups", "tags", "dropdownMenuTags"], []);
  const report_status = get(filters, ["report_status", "tags", "tags"], []);
  const modalities = get(filters, ["modalities", "tags", "tags"], []);
  const questionnaire_response = get(
    filters,
    ["questionnaire_response", "tags", "tags"],
    []
  );
  const use_case = get(filters, ["use_case", "tags", "tags"], []);
  // LC-specific filters (now arrays like task_status)
  const program_type_raw = get(filters, ["program_type"], []);
  const program_type = Array.isArray(program_type_raw)
    ? program_type_raw
    : program_type_raw
    ? [program_type_raw]
    : [];

  const ai_recommendation_raw = get(filters, ["ai_recommendation"], []);
  const ai_recommendation = Array.isArray(ai_recommendation_raw)
    ? ai_recommendation_raw
    : ai_recommendation_raw
    ? [ai_recommendation_raw]
    : [];

  const manual_review_raw = get(filters, ["manual_review"], []);
  const manual_review = Array.isArray(manual_review_raw)
    ? manual_review_raw
    : manual_review_raw
    ? [manual_review_raw]
    : [];

  // Adding a filter on patient additional. No manipulation is required here as this is only applied from the BE as saved filter.
  const additionalFilter = get(filters, ["additional"], null);

  Object.keys(filters).forEach((category) => {
    if (category === "dateRange") return;
    if (category === "reminders") return; // Skip reminder - it's handled separately

    if (category === "site") {
      sites.push([...filters["site"]["tags"]["dropdownMenuTags"]]);
      return;
    }

    if (category === "report_edited_by") {
      report_editors.push(
        ...get(filters, ["report_edited_by", "tags", "dropdownMenuTags"], [])
      );
      return;
    }

    if (category === "dicom_upload") {
      get(filters, ["dicom_upload", "tags", "tags"], []).map((status) => {
        if (status === "pending") {
          dicom_upload.push(1);
        } else if (status === "negative") {
          dicom_upload.push(2);
        } else if (status === "positive") {
          dicom_upload.push(3);
        }
      });
      return;
    }

    if (
      category === "risk_groups" ||
      category === "report_status" ||
      category === "modalities" ||
      category === "use_case" ||
      category === "questionnaire_response" ||
      category === "program_type" ||
      category === "ai_recommendation" ||
      category === "manual_review"
    ) {
      return;
    }

    if (category === "symptoms") {
      return;
    }

    if (
      category === "present_workflow_findings" ||
      category === "absent_workflow_findings"
    ) {
      const values = get(filters, [category, "tags", "tags"], []);
      const join = get(filters, [category, "join"], null);

      let updated_category = category;
      if (category === "present_workflow_findings") {
        updated_category = "present_findings";
      }

      if (category === "absent_workflow_findings") {
        updated_category = "absent_findings";
      }

      values.forEach((selectedTags) => {
        categories?.["workflow_tags"]?.[updated_category]?.["tags"]?.push(selectedTags);
      });
      categories["workflow_tags"][updated_category]["join"] = join;
      return;
    }

    const tags = get(filters, [category, "tags"], null);
    if (!tags) return;

    Object.keys(tags).forEach((tagType) => {
      // Only positive findings must be shown
      if (tagType === "tags" || tagType === "dropdownMenuTags") {
        let values = tags[tagType];
        if (
          (category === "present_findings" || category === "absent_findings") &&
          tagType === "dropdownMenuTags"
        ) {
          values = Object.values(values).flat();
        }
        values.forEach((selectedTags) => {
          categories?.["ai_tags"]?.[category]?.["tags"]?.push(selectedTags);
        });
      } else if (tagType === "dropdownTags") {
        tags.dropdownTags.forEach((tag) => {
          if (!isEmpty(tag[Object.keys(tag)[0]])) {
            categories[category][Object.keys(tag)[0]] = [...tag[Object.keys(tag)[0]]];
          }
        });
      }
    });

    const join = get(filters, [category, "join"], null);
    if (join) {
      categories["ai_tags"][category]["join"] = join;
    }
  });

  patient_type?.tags?.tags.forEach((tag) => {
    if (tag === "male") {
      genders.push("0");
    } else if (tag === "female") {
      genders.push("1");
    } else if (tag === "other") {
      genders.push("2");
    } else {
      ageGroups.push(tag);
    }
  });
  // Start with existing custom_tags
  let custom_tags = filters?.custom_tags || filters?.custom_tag || [];

  // Convert special filters (program_type, ai_recommendation, manual_review) to custom tags
  // if specialTagIds are provided
  if (specialTagIds) {
    // Remove any existing entries for these special tags from custom_tags
    custom_tags = custom_tags.filter((tag) => {
      const tagId = tag.tag_id;
      return (
        tagId != specialTagIds.program_type &&
        tagId != specialTagIds.ai_recommendation &&
        tagId != specialTagIds.manual_review
      );
    });

    // Add program_type as custom tag if it has values
    if (program_type.length > 0 && specialTagIds.program_type) {
      custom_tags.push({
        tag_id: specialTagIds.program_type,
        value: program_type,
        logic: "in", // multi_dropdown tags use "contains" logic
      });
    }

    // Add ai_recommendation as custom tag if it has values
    if (ai_recommendation.length > 0 && specialTagIds.ai_recommendation) {
      custom_tags.push({
        tag_id: specialTagIds.ai_recommendation,
        value: ai_recommendation,
        logic: "in", // multi_dropdown tags use "contains" logic
      });
    }

    // Add manual_review as custom tag if it has values
    if (manual_review.length > 0 && specialTagIds.manual_review) {
      custom_tags.push({
        tag_id: specialTagIds.manual_review,
        value: manual_review,
        logic: "in", // multi_dropdown tags use "contains" logic
      });
    }
  }

  const formattedFilters = {
    start_study_date: get(dateRange, [0], ""),
    end_study_date: get(dateRange, [1], ""),
    ai_tags: categories.ai_tags,
    workflow_tags: categories.workflow_tags,
    lab_results: categories.lab_results,
    risk_groups: risk_groups.join(","),
    symptoms: symptoms.join(","),
    report_status: report_status.join(","),
    modalities: modalities.join(","),
    gender: genders.join(","),
    age_groups: ageGroups.join(","),
    ai_inference: dicom_upload.join(","),
    report_edited_by: report_editors.map((i) => i.email).join(","),
    source: sites.join(","),
    use_case: use_case.join(","),
    questionnaire_response: questionnaire_response.join(","),
    additional: additionalFilter,
    custom_tags: custom_tags,
    // Only include these fields if specialTagIds are NOT provided (backward compatibility)
    ...(specialTagIds
      ? {}
      : {
          program_type: program_type.join(","),
          ai_recommendation: ai_recommendation.join(","),
          manual_review: manual_review.join(","),
        }),
  };

  // Handle reminders: preserve existing reminder from saved filters, or build from UI structure
  const existingReminder = get(filters, ["reminders"], null);
  if (existingReminder) {
    // If reminder already exists (from saved filter), ensure it has timezone_offset
    const timezoneOffset = getTimezoneOffset();
    formattedFilters.reminders = {
      ...existingReminder,
      timezone_offset: existingReminder.timezone_offset ?? timezoneOffset,
    };
  } else {
    // Otherwise, build reminders structure from UI filter structure
    const reminders = buildReminders(filters);
    if (reminders) {
      formattedFilters.reminders = reminders;
    }
  }

  return formattedFilters;
};

/**
 * Formats a custom tag value for display in filter strings
 * @param {any} value - The value to format (can be boolean, array, string, number, date)
 * @param {string} tagType - The type of the tag (e.g., "date", "integer", "float", "boolean", "string", "multi_dropdown", etc.)
 * @param {string} logic - The logic operator (e.g., "exact", "gt", "gte", "lt", "lte", "between", "contains")
 * @param {boolean} showLogic - Whether to show the logic symbol prefix (default: true)
 * @returns {string} - The formatted display value
 */
const formatCustomTagValue = (customTag, tagType, logic = "exact", showLogic = true) => {
  let value = customTag.value;

  // Handle boolean values
  if (tagType === "boolean") {
    return value ? "Yes" : "No";
  }

  // Handle array values, join with comma
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  // Handle null/undefined/empty values
  if (value == null || value === "") {
    return "";
  }

  // Handle values with logic symbols (for numeric types)
  if (showLogic && (tagType === "integer" || tagType === "float")) {
    const logicSymbol = getLogicSymbol(logic || "", tagType);
    return `${logicSymbol}${value}`;
  }

  // Handle dates without logic symbols
  if (tagType === "date") {
    try {
      return format(new Date(value), "MMM dd, yyyy");
    } catch (error) {
      return String(value);
    }
  }

  // Default: return value as string
  return String(value);
};

const getFilterString = (
  filtersData,
  labelMappings,
  allFilters,
  sources,
  taskOwnerLabelMappings = {},
  customFieldsList = []
) => {
  const filtersList = [];
  filtersData &&
    Object.keys(filtersData).map((filterKey) => {
      const filter = filtersData[filterKey];
      if (filterKey === "dateRange") {
        if (filter[0] !== null || filter[1] !== null)
          filtersList.push(
            format(new Date(filter[0]), "PP") + " - " + format(new Date(filter[1]), "PP")
          );
      } else if (filterKey === "symptoms" || filterKey === "risk_groups") {
        const tags = get(filter, ["tags"], {});
        tags?.dropdownMenuTags?.map((tag) => {
          let label;
          allFilters.map((filterObj) => {
            if (Object.keys(filterObj)[0] === filterKey) {
              const dropdownMenuTags = get(filterObj, [
                filterKey,
                "tags",
                "dropdownMenuTags",
              ]);
              dropdownMenuTags.map((tagObj) => {
                if (tagObj.id === tag) label = tagObj.choice;
              });
            }
          });
          filtersList.push(label);
        });
      } else if (
        filterKey === "program_type" ||
        filterKey === "ai_recommendation" ||
        filterKey === "manual_review"
      ) {
        // LC-specific filters (now arrays like task_status), options come from workspace tags endpoint
        const selectedValues = Array.isArray(filter) ? filter : [];
        if (selectedValues.length > 0) {
          const filterLabels = selectedValues
            .map((value) => {
              // Try labelMappings first
              const mappedLabel = labelMappings[value];
              if (mappedLabel) return mappedLabel;
              return value;
            })
            .filter(Boolean);
          if (filterLabels.length > 0) {
            const filterName =
              filterKey === "program_type"
                ? "Program Type"
                : filterKey === "ai_recommendation"
                ? "AI Recommendation"
                : "Manual Review";
            filtersList.push(`${filterName}: ${filterLabels.join(", ")}`);
          }
        }
      } else if (filterKey === "report_edited_by") {
        const tags = get(filter, ["tags", "dropdownMenuTags"], []);
        filtersList.push(tags.map((i) => i.email));
      } else if (filterKey === "taskDueDateRange") {
        // Task due date range filter
        if (filter[0] !== null && filter[1] !== null) {
          const dateRange =
            format(new Date(filter[0]), "PP") + " - " + format(new Date(filter[1]), "PP");
          filtersList.push(`Task due date: ${dateRange}`);
        }
      } else if (filterKey === "task_status") {
        // Task status filter - array of status values
        const taskStatuses = Array.isArray(filter) ? filter : [];
        const statusLabels = [];
        taskStatuses.forEach((status) => {
          if (
            status === "overdue" ||
            status === "urgent" ||
            status === "upcoming" ||
            status === "done"
          ) {
            statusLabels.push(t(`key.task_status.${status}`));
          }
        });
        if (statusLabels.length > 0) {
          filtersList.push(`Task status: ${statusLabels.join(", ")}`);
        }
      } else if (filterKey === "task_owner") {
        // task_owner is a simple array of user IDs
        const taskOwnerIds = Array.isArray(filter) ? filter : [];
        // Convert IDs to names using taskOwnerLabelMappings
        const taskOwnerNames = taskOwnerIds
          .map((id) => taskOwnerLabelMappings[String(id)] || id)
          .filter(Boolean);
        if (taskOwnerNames.length > 0) {
          filtersList.push(`Task owners: ${taskOwnerNames.join(", ")}`);
        }
      } else if (filterKey === "site") {
        const { tags } = filter;

        const sourceMap = {};
        sources.forEach((source) => {
          sourceMap[source.uuid] = source.name;
        });

        tags?.dropdownMenuTags?.map((tag) => filtersList.push(sourceMap[tag]));
      } else if (filterKey === "custom_tags") {
        const customTags = Array.isArray(filter) ? filter : [];
        if (customTags.length > 0 && customFieldsList.length > 0) {
          // Group customTags by tag_id to handle date ranges (between logic)
          const groupedByTagId = {};
          customTags.forEach((customTag) => {
            const tagId = customTag.tag_id;
            if (!groupedByTagId[tagId]) {
              groupedByTagId[tagId] = [];
            }
            groupedByTagId[tagId].push(customTag);
          });

          // Process each group
          Object.keys(groupedByTagId).forEach((tagId) => {
            const tagGroup = groupedByTagId[tagId];
            const customField = customFieldsList.find((field) => +field.id === +tagId);

            if (!customField) return;

            const tagName = customField.tag;
            let tagValue = "";

            // Special handling for date fields with multiple entries (between logic)
            if (customField.tag_type === "date" && tagGroup.length > 1) {
              const gteEntry = tagGroup.find((tag) => tag.logic === "gte");
              const lteEntry = tagGroup.find((tag) => tag.logic === "lte");

              if (gteEntry && lteEntry) {
                try {
                  const formattedStart = format(new Date(gteEntry.value), "MMM dd, yyyy");
                  const formattedEnd = format(new Date(lteEntry.value), "MMM dd, yyyy");
                  tagValue = `Between ${formattedStart} - ${formattedEnd}`;
                } catch (error) {
                  tagValue = `Between ${gteEntry.value} - ${lteEntry.value}`;
                }
              } else {
                // Fallback: use first entry
                tagValue = formatCustomTagValue(
                  tagGroup[0],
                  customField.tag_type,
                  tagGroup[0].logic
                );
              }
            } else if (customField.tag_type === "date" && tagGroup.length === 1) {
              // Single date entry - check logic for before/after/on
              const customTag = tagGroup[0];
              const logic = customTag.logic;

              try {
                const formattedDate = format(new Date(customTag.value), "MMM dd, yyyy");

                if (logic === "lt") {
                  tagValue = `Before ${formattedDate}`;
                } else if (logic === "gt") {
                  tagValue = `After ${formattedDate}`;
                } else if (logic === "exact") {
                  tagValue = `On ${formattedDate}`;
                } else {
                  // For other logic types (gte, lte), use formatCustomTagValue
                  tagValue = formatCustomTagValue(customTag, customField.tag_type, logic);
                }
              } catch (error) {
                // Fallback if date parsing fails
                if (logic === "lt") {
                  tagValue = `Before ${customTag.value}`;
                } else if (logic === "gt") {
                  tagValue = `After ${customTag.value}`;
                } else if (logic === "exact") {
                  tagValue = `On ${customTag.value}`;
                } else {
                  tagValue = formatCustomTagValue(customTag, customField.tag_type, logic);
                }
              }
            } else {
              // Non-date fields or multiple non-date entries - use existing logic
              tagValue = formatCustomTagValue(
                tagGroup[0],
                customField.tag_type,
                tagGroup[0].logic
              );
            }

            if (tagValue) {
              filtersList.push(`${tagName}: ${tagValue}`);
            }
          });
        }
      } else {
        const tags = get(filter, ["tags"], {});
        tags?.dropdownTags?.map((tag) =>
          filtersList.push(t(`mapping.${Object.keys(tag)[0]}`))
        );
        let _dropdownMenuTags = tags?.dropdownMenuTags;
        if (
          filterKey === "present_findings" ||
          filterKey === "absent_findings" ||
          filterKey === "absent_workflow_findings" ||
          filterKey === "present_workflow_findings"
        ) {
          _dropdownMenuTags = Object.values(tags?.dropdownMenuTags).flat();
        }
        _dropdownMenuTags?.map((tag) => filtersList.push(t(`mapping.${tag}`)));
        tags?.tags?.map((tag) => filtersList.push(t(`mapping.${tag}`)));
      }
    });

  return filtersList.filter((element) => element?.length).join(", ");
};

/**
 * Builds MultiSelectFilterRequest structure for LC multi-select filters
 * @param {Array} savedFiltersArray - Array of saved filter objects (each with filter/values property)
 * @param {Object} options - Additional options for the request
 * @param {string} options.logic - "AND" or "OR" (default: "AND")
 * @param {number} options.page_size - Page size (default: 10)
 * @param {string} options.sort_by - Sort by field: "study" | "id" | "created" | "recent" (default: "study")
 * @param {string} options.sort_order - Sort order: "asc" | "desc" (default: "desc")
 * @param {number|null} options.pagination_id - Pagination ID (default: null)
 * @param {string} options.page - "next" | "prev" (default: "next")
 * @param {boolean} options.unread_messages - Only show patients with unread messages (default: false)
 * @param {boolean} options.only_counts - Return only count (default: false)
 * @param {string} options.search - Search string
 * @param {Object} options.selectedWorkspace - Selected workspace object (for source fallback)
 * @param {Object} options.specialTagIds - Special custom tag IDs for program_type, ai_recommendation, manual_review
 * @param {Array} options.customFields - Array of custom field definitions (for setting logic based on tag type)
 * @returns {Object} MultiSelectFilterRequest structure
 */
const buildMultiSelectFilterRequest = (savedFiltersArray = [], options = {}) => {
  const {
    logic = "AND",
    page_size = 10,
    sort_by = "study",
    sort_order = "desc",
    pagination_id = null,
    page = "next",
    unread_messages = false,
    only_counts = false,
    search = "",
    selectedWorkspace = null,
    specialTagIds = null,
    customFields = null,
  } = options;

  // Convert each saved filter to the format expected by the API using formatFiltersObj
  const filters = savedFiltersArray.map((savedFilter) => {
    const filterData = savedFilter?.filter || savedFilter?.values || savedFilter;

    // Format the filter using formatFiltersObj to match API payload format
    // Pass specialTagIds to convert program_type, ai_recommendation, manual_review to custom tags
    let formattedFilter = formatFiltersObj(filterData, specialTagIds);

    // Handle date formatting (same as in getFilterParams)
    if (formattedFilter?.start_study_date) {
      let startDate = formattedFilter.start_study_date;
      let endDate = formattedFilter.end_study_date;

      if (startDate && endDate) {
        const startDateVal = new Date(startDate);
        const endDateVal = new Date(endDate);
        endDateVal.setDate(endDateVal.getDate() + 1); // Add 1 day to end date

        if (isValidDate(startDateVal) && isValidDate(endDateVal)) {
          startDateVal.setHours(0, 0, 0, 0);
          endDateVal.setHours(0, 0, 0, 0);
          formattedFilter.start_study_date = startDateVal.toISOString();
          formattedFilter.end_study_date = endDateVal.toISOString();
        }
      }
    }

    // Handle source fallback for LC (same as in getFilterParams)
    const isLC = process.env.NEXT_PUBLIC_IS_LC === "true";
    if (isLC && selectedWorkspace) {
      if (!formattedFilter.source || formattedFilter.source === "") {
        formattedFilter.source = selectedWorkspace?.id;
      }
    }

    // Set logic for custom_tags based on tag type
    // For multi_dropdown tags, use "contains", otherwise use "in"
    if (
      formattedFilter.custom_tags &&
      Array.isArray(formattedFilter.custom_tags) &&
      customFields
    ) {
      formattedFilter.custom_tags = formattedFilter.custom_tags.map((customTag) => {
        // If logic is already set, preserve it (e.g., from saved filters)
        if (customTag.logic) {
          return customTag;
        }

        // Look up the tag type from customFields using tag_id
        const tagId = customTag.tag_id;
        const customField = customFields.find(
          (field) => field.id === tagId || field.uuid === tagId
        );

        // Set logic based on tag type: "contains" for multi_dropdown, "in" otherwise
        const tagLogic = customField?.tag_type === "multi_dropdown" ? "contains" : "in";

        return {
          ...customTag,
          logic: tagLogic,
        };
      });
    }

    return formattedFilter;
  });

  // Helper function to create a default empty filter object
  const createDefaultFilterObject = () => {
    const defaultFilter = {
      start_study_date: "",
      end_study_date: "",
      ai_tags: {
        present_findings: {
          tags: [],
          join: "",
        },
        absent_findings: {
          tags: [],
          join: "",
        },
      },
      workflow_tags: {
        present_findings: {
          tags: [],
          join: "",
        },
        absent_findings: {
          tags: [],
          join: "",
        },
      },
      lab_results: {},
      risk_groups: "",
      symptoms: "",
      report_status: "",
      modalities: "",
      gender: "",
      age_groups: "",
      ai_inference: "",
      report_edited_by: "",
      source: "",
      use_case: "",
      questionnaire_response: "",
      additional: null,
      custom_tags: [],
      // Only include these fields if specialTagIds are NOT provided (backward compatibility)
      ...(specialTagIds
        ? {}
        : {
            program_type: "",
            ai_recommendation: "",
            manual_review: "",
          }),
    };

    // Handle source fallback for LC (same as in getFilterParams)
    const isLC = process.env.NEXT_PUBLIC_IS_LC === "true";
    if (isLC && selectedWorkspace) {
      defaultFilter.source = selectedWorkspace?.id || "";
    }

    return defaultFilter;
  };

  if (search && search.trim().length > 0) {
    const searchFilter = createDefaultFilterObject();
    searchFilter.search = search.trim();
    filters.push(searchFilter);
  }

  // If no filters exist (no saved filters and no search), add a default empty filter object
  if (filters.length === 0) {
    filters.push(createDefaultFilterObject());
  }

  // Build the request structure
  const request = {
    logic,
    filters,
    unread_messages,
    page_size,
    sort_by,
    sort_order,
    only_counts,
  };

  // Add optional fields only if they have values
  if (pagination_id !== null) {
    request.pagination_id = pagination_id;
  }

  if (page !== "next") {
    request.page = page;
  }

  return request;
};

export {
  formatFiltersObj,
  getFilterString,
  formatCustomTagValue,
  parseRemindersToTaskFilters,
  buildMultiSelectFilterRequest,
  buildReminders,
};
